import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RotateCcw, Clock, Calculator, Activity, Syringe, Heart } from 'lucide-react';

interface HeparinDose {
  id: string;
  amount: number; // IE
  timestamp: Date;
  relativeTime: number; // Minuten seit Initialdosis
  isInitial?: boolean;
}

interface ACTMeasurement {
  id: string;
  value: number; // Sekunden
  timestamp: Date;
  relativeTime: number;
}

type ProtaminType = 'sulfat' | 'hydrochlorid';

interface ProtaminConfig {
  sulfat: { concentration: number; iePerMl: number };
  hydrochlorid: { concentration: number; iePerMl: number };
}

const PROTAMIN_CONFIG: ProtaminConfig = {
  sulfat: { concentration: 10, iePerMl: 1400 }, // 10mg/ml, 1400IE/ml
  hydrochlorid: { concentration: 50, iePerMl: 5000 } // 50mg/ml, 5000IE/ml
};

const HEPARIN_HALF_LIFE = 90; // Minuten
const AMPULE_VOLUME = 5; // ml

export default function Protaminator() {
  const [bodyWeight, setBodyWeight] = useState<number>(70);
  const [initialDoseTime, setInitialDoseTime] = useState<Date>(new Date());
  const [actualInitialDose, setActualInitialDose] = useState<number>(0);
  const [hlmPriming, setHlmPriming] = useState<number>(10000);
  const [heparinDoses, setHeparinDoses] = useState<HeparinDose[]>([]);
  const [actMeasurements, setActMeasurements] = useState<ACTMeasurement[]>([]);
  const [protaminType, setProtaminType] = useState<ProtaminType>('sulfat');
  const [calculationTime, setCalculationTime] = useState<Date>(new Date());
  const [considerHalfLife, setConsiderHalfLife] = useState<boolean>(true);
  
  // Temporäre Eingabefelder
  const [tempHeparinAmount, setTempHeparinAmount] = useState<string>('');
  const [tempHeparinTime, setTempHeparinTime] = useState<string>('');
  const [tempActValue, setTempActValue] = useState<string>('');
  const [tempActTime, setTempActTime] = useState<string>('');

  const suggestedInitialDose = bodyWeight * 300;

  useEffect(() => {
    if (actualInitialDose === 0) {
      setActualInitialDose(suggestedInitialDose);
    }
  }, [suggestedInitialDose, actualInitialDose]);

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatRelativeTime = (minutes: number): string => {
    if (minutes === 0) return '0 min';
    if (minutes < 0) return `${Math.abs(minutes)} min vorher`;
    return `+${minutes} min`;
  };

  const getRelativeTime = (timestamp: Date, reference: Date): number => {
    return Math.round((timestamp.getTime() - reference.getTime()) / (1000 * 60));
  };

  const setCurrentTime = (setter: (date: Date) => void) => {
    setter(new Date());
  };

  const addHeparinDose = () => {
    if (!tempHeparinAmount || parseFloat(tempHeparinAmount) <= 0) return;
    
    const timestamp = tempHeparinTime ? new Date(tempHeparinTime) : new Date();
    const amount = parseFloat(tempHeparinAmount);
    const relativeTime = getRelativeTime(timestamp, initialDoseTime);
    
    const newDose: HeparinDose = {
      id: Date.now().toString(),
      amount,
      timestamp,
      relativeTime
    };
    
    setHeparinDoses(prev => [...prev, newDose].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()));
    setTempHeparinAmount('');
    setTempHeparinTime('');
  };

  const addACTMeasurement = () => {
    if (!tempActValue || parseFloat(tempActValue) <= 0) return;
    
    const timestamp = tempActTime ? new Date(tempActTime) : new Date();
    const value = parseFloat(tempActValue);
    const relativeTime = getRelativeTime(timestamp, initialDoseTime);
    
    const newMeasurement: ACTMeasurement = {
      id: Date.now().toString(),
      value,
      timestamp,
      relativeTime
    };
    
    setActMeasurements(prev => [...prev, newMeasurement].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()));
    setTempActValue('');
    setTempActTime('');
  };

  const getTotalHeparin = (): number => {
    return actualInitialDose + hlmPriming + heparinDoses.reduce((sum, dose) => sum + dose.amount, 0);
  };

  const getActiveHeparin = (atTime: Date): number => {
    const totalHeparin = getTotalHeparin();
    if (!considerHalfLife) return totalHeparin;
    
    const minutesElapsed = getRelativeTime(atTime, initialDoseTime);
    if (minutesElapsed <= 0) return totalHeparin;
    
    const halfLives = minutesElapsed / HEPARIN_HALF_LIFE;
    const remaining = totalHeparin * Math.pow(0.5, halfLives);
    return Math.max(0, remaining);
  };

  const getDegradedHeparin = (atTime: Date): number => {
    return getTotalHeparin() - getActiveHeparin(atTime);
  };

  const calculateProtaminDose = (atTime: Date) => {
    const activeHeparin = getActiveHeparin(atTime);
    const protaminNeededMg = activeHeparin / 100; // 1mg Protamin pro 100IE Heparin
    const protaminNeededIE = protaminNeededMg * (PROTAMIN_CONFIG[protaminType].iePerMl / PROTAMIN_CONFIG[protaminType].concentration);
    const volumeNeeded = protaminNeededMg / PROTAMIN_CONFIG[protaminType].concentration;
    const ampulesNeeded = volumeNeeded / AMPULE_VOLUME;
    const wholeAmpules = Math.floor(ampulesNeeded);
    const partialVolume = volumeNeeded - (wholeAmpules * AMPULE_VOLUME);
    
    return {
      protaminMg: protaminNeededMg,
      protaminIE: protaminNeededIE,
      volumeMl: volumeNeeded,
      ampulesTotal: ampulesNeeded,
      wholeAmpules,
      partialVolume
    };
  };

  const resetForm = () => {
    setBodyWeight(70);
    setInitialDoseTime(new Date());
    setActualInitialDose(0);
    setHlmPriming(10000);
    setHeparinDoses([]);
    setActMeasurements([]);
    setCalculationTime(new Date());
    setTempHeparinAmount('');
    setTempHeparinTime('');
    setTempActValue('');
    setTempActTime('');
  };

  const protaminCalc = calculateProtaminDose(calculationTime);

  return (
    <div className="max-w-6xl mx-auto p-4 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Heart className="w-8 h-8 text-red-500" />
            <h1 className="text-3xl font-bold text-gray-800">Protaminator</h1>
          </div>
          <button
            onClick={resetForm}
            className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Eingaben */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 space-y-4">
            <h2 className="text-xl font-semibold text-blue-800 flex items-center gap-2 border-b border-blue-300 pb-2">
              <Calculator className="w-5 h-5" />
              Grunddaten
            </h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Körpergewicht (kg)
              </label>
              <input
                type="number"
                value={bodyWeight}
                onChange={(e) => setBodyWeight(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Vorgeschlagene Initialdosis: {suggestedInitialDose.toLocaleString()} IE
              </label>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Tatsächliche Initialdosis (IE)
              </label>
              <input
                type="number"
                value={actualInitialDose}
                onChange={(e) => setActualInitialDose(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Zeitpunkt Initialdosis
              </label>
              <div className="flex gap-2">
                <input
                  type="datetime-local"
                  value={initialDoseTime.toISOString().slice(0, 16)}
                  onChange={(e) => setInitialDoseTime(new Date(e.target.value))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={() => setCurrentTime(setInitialDoseTime)}
                  className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
                >
                  <Clock className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                HLM-Priming (IE)
              </label>
              <input
                type="number"
                value={hlmPriming}
                onChange={(e) => setHlmPriming(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="halfLife"
                checked={considerHalfLife}
                onChange={(e) => setConsiderHalfLife(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="halfLife" className="text-sm text-gray-600">
                Halbwertszeit berücksichtigen (90 min)
              </label>
            </div>
          </div>

          {/* Weitere Heparingaben */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5 space-y-4">
            <h2 className="text-xl font-semibold text-amber-800 flex items-center gap-2 border-b border-amber-300 pb-2">
              <Syringe className="w-5 h-5" />
              Weitere Gaben
            </h2>
            
            <div className="bg-white border border-amber-300 p-4 rounded-lg shadow-sm">
              <h3 className="font-medium text-amber-800 mb-3">Heparin hinzufügen</h3>
              <div className="space-y-2">
                <input
                  type="number"
                  placeholder="Menge (IE)"
                  value={tempHeparinAmount}
                  onChange={(e) => setTempHeparinAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex gap-2">
                  <input
                    type="datetime-local"
                    value={tempHeparinTime}
                    onChange={(e) => setTempHeparinTime(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => setTempHeparinTime(new Date().toISOString().slice(0, 16))}
                    className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    <Clock className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={addHeparinDose}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                >
                  <Plus className="w-4 h-4" />
                  Heparin hinzufügen
                </button>
              </div>
            </div>

            <div className="bg-white border border-amber-300 p-4 rounded-lg shadow-sm">
              <h3 className="font-medium text-amber-800 mb-3">ACT-Messung</h3>
              <div className="space-y-2">
                <input
                  type="number"
                  placeholder="ACT (Sekunden)"
                  value={tempActValue}
                  onChange={(e) => setTempActValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex gap-2">
                  <input
                    type="datetime-local"
                    value={tempActTime}
                    onChange={(e) => setTempActTime(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => setTempActTime(new Date().toISOString().slice(0, 16))}
                    className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    <Clock className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={addACTMeasurement}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  <Plus className="w-4 h-4" />
                  ACT hinzufügen
                </button>
              </div>
            </div>
          </div>

          {/* Übersicht und Berechnung */}
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 space-y-4">
            <h2 className="text-xl font-semibold text-green-800 flex items-center gap-2 border-b border-green-300 pb-2">
              <Activity className="w-5 h-5" />
              Übersicht & Berechnung
            </h2>
            
            <div className="bg-white border border-green-300 p-4 rounded-lg shadow-sm">
              <h3 className="font-medium text-green-800 mb-2">Heparin-Status</h3>
              <div className="text-sm space-y-1">
                <div>Gesamt verabreicht: <span className="font-semibold">{getTotalHeparin().toLocaleString()} IE</span></div>
                {considerHalfLife && (
                  <>
                    <div>Abgebaut: <span className="font-semibold">{getDegradedHeparin(calculationTime).toLocaleString('de-DE', {maximumFractionDigits: 0})} IE</span></div>
                    <div>Noch aktiv: <span className="font-semibold">{getActiveHeparin(calculationTime).toLocaleString('de-DE', {maximumFractionDigits: 0})} IE</span></div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white border border-green-300 p-4 rounded-lg shadow-sm">
              <h3 className="font-medium text-green-800 mb-3">Protamin-Berechnung</h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Protamin-Typ
                  </label>
                  <select
                    value={protaminType}
                    onChange={(e) => setProtaminType(e.target.value as ProtaminType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="sulfat">Protaminsulfat (10mg/ml)</option>
                    <option value="hydrochlorid">Protaminhydrochlorid (50mg/ml)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Berechnungszeitpunkt
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="datetime-local"
                      value={calculationTime.toISOString().slice(0, 16)}
                      onChange={(e) => setCalculationTime(new Date(e.target.value))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => setCurrentTime(setCalculationTime)}
                      className="px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                    >
                      <Clock className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="bg-green-100 border-2 border-green-400 p-3 rounded-lg">
                  <div className="text-sm space-y-1">
                    <div className="font-semibold text-green-800">Benötigte Protamin-Dosis:</div>
                    <div>{protaminCalc.protaminMg.toFixed(1)} mg</div>
                    <div>{protaminCalc.protaminIE.toFixed(0)} IE</div>
                    <div>{protaminCalc.volumeMl.toFixed(1)} ml</div>
                    <div className="mt-2 font-semibold">Ampullen:</div>
                    <div>{protaminCalc.ampulesTotal.toFixed(1)} Ampullen</div>
                    {protaminCalc.wholeAmpules > 0 && (
                      <div className="text-xs text-gray-600">
                        = {protaminCalc.wholeAmpules} ganze Ampulle{protaminCalc.wholeAmpules !== 1 ? 'n' : ''}
                        {protaminCalc.partialVolume > 0.1 && ` + ${protaminCalc.partialVolume.toFixed(1)} ml`}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabellen */}
        {(heparinDoses.length > 0 || actMeasurements.length > 0) && (
          <div className="mt-6 bg-slate-50 border-2 border-slate-200 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 border-b border-slate-300 pb-2">
              Verlaufsdokumentation
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {heparinDoses.length > 0 && (
                <div className="bg-white border-2 border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <Syringe className="w-4 h-4" />
                    Heparin-Gaben
                  </h3>
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Zeit</th>
                          <th className="px-3 py-2 text-left">Relativ</th>
                          <th className="px-3 py-2 text-right">Menge (IE)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t bg-blue-50">
                          <td className="px-3 py-2">{formatTime(initialDoseTime)}</td>
                          <td className="px-3 py-2">Initial</td>
                          <td className="px-3 py-2 text-right font-semibold">{actualInitialDose.toLocaleString()}</td>
                        </tr>
                        <tr className="border-t bg-gray-50">
                          <td className="px-3 py-2">-</td>
                          <td className="px-3 py-2">HLM-Priming</td>
                          <td className="px-3 py-2 text-right">{hlmPriming.toLocaleString()}</td>
                        </tr>
                        {heparinDoses.map((dose) => (
                          <tr key={dose.id} className="border-t">
                            <td className="px-3 py-2">{formatTime(dose.timestamp)}</td>
                            <td className="px-3 py-2">{formatRelativeTime(dose.relativeTime)}</td>
                            <td className="px-3 py-2 text-right">{dose.amount.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {actMeasurements.length > 0 && (
                <div className="bg-white border-2 border-purple-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-purple-800 mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    ACT-Messungen
                  </h3>
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Zeit</th>
                          <th className="px-3 py-2 text-left">Relativ</th>
                          <th className="px-3 py-2 text-right">ACT (s)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {actMeasurements.map((measurement) => (
                          <tr key={measurement.id} className="border-t">
                            <td className="px-3 py-2">{formatTime(measurement.timestamp)}</td>
                            <td className="px-3 py-2">{formatRelativeTime(measurement.relativeTime)}</td>
                            <td className="px-3 py-2 text-right">{measurement.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}